import { Loader2, LogOut } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/cn';
import { Logo } from './logo';

// docs/06 §4: dark forest sidebar with white logo and grouped nav; host label; user at the bottom.
// On wider screens the sidebar stays in view while the page scrolls, so Sign out is always visible; a long menu scrolls
// inside the sidebar.

export interface NavGroup { label?: string; items: Array<{ key: string; label: string; count?: number; urgent?: boolean }> }

// Breadcrumb (docs/06 §4): worked out from the menu (section / page); a detail page adds its own last crumb, e.g. the
// application reference, with usePageCrumb.
const CrumbContext = React.createContext<(crumb: string | null) => void>(() => {});
/** A detail page names itself in the breadcrumb ("APP-26-0418"); cleared when the page goes away. */
export function usePageCrumb(crumb: string | null | undefined) {
  const set = React.useContext(CrumbContext);
  React.useEffect(() => { set(crumb ?? null); return () => set(null); }, [crumb, set]);
}

export interface AppShellProps {
  host: string;                 // e.g. compliance.paystreet.io
  nav: NavGroup[];
  active: string;
  onNavigate: (key: string) => void;
  user: { displayName: string; detail?: string };
  onSignOut: () => void;
  signingOut?: boolean;
  signOutError?: string | null;
  topBar?: React.ReactNode;
  /** Breadcrumb root: the customer app starts every trail at "Dashboard" (Wallex pattern); staff apps use the section. */
  crumbRoot?: string;
  /** Right of the breadcrumb: the customer app's company selector, language chip and avatar. */
  headerRight?: React.ReactNode;
  /** Staff workspaces with side panels use the full width of large screens; the customer app stays at 1280px. */
  wide?: boolean;
  children: React.ReactNode;
}

export function AppShell({ host, nav, active, onNavigate, user, onSignOut, signingOut = false, signOutError, topBar, crumbRoot, headerRight, wide = false, children }: AppShellProps) {
  const [pageCrumb, setPageCrumb] = React.useState<string | null>(null);
  const group = nav.find(g => g.items.some(i => i.key === active));
  const item = group?.items.find(i => i.key === active);
  // Each crumb that names a screen links back to it (prototype: the breadcrumb is the way back from a detail page).
  const home = nav[0]?.items[0];
  type Crumb = { label: string; key?: string };
  const crumbs: Crumb[] = [];
  if (crumbRoot) {
    crumbs.push(home?.label === crumbRoot ? { label: crumbRoot, key: home.key } : { label: crumbRoot });
    crumbs.push(item && item.label !== crumbRoot ? { label: item.label, key: item.key } : { label: 'Overview' });
  } else {
    if (group?.label) crumbs.push({ label: group.label });
    if (item) crumbs.push({ label: item.label, key: item.key });
  }
  if (pageCrumb) crumbs.push({ label: pageCrumb });
  return (
    <CrumbContext.Provider value={setPageCrumb}>
    <div className="flex min-h-full flex-col md:flex-row">
      <nav aria-label="Main" className="flex shrink-0 flex-col bg-forest text-white md:sticky md:top-0 md:h-screen md:w-60">
        <div className="px-5 pt-6"><Logo variant="white" /></div>
        <p className="px-5 pt-2 font-mono text-[11px] text-white/60">{host}</p>
        <div className="flex gap-1 overflow-x-auto px-3 py-4 md:min-h-0 md:flex-1 md:flex-col md:overflow-y-auto md:overflow-x-hidden">
          {nav.map((g, i) => (
            <div key={g.label ?? i} className="flex gap-1 md:flex-col md:pt-3">
              {g.label && <p className="hidden px-2 pb-1 text-[11px] font-semibold uppercase tracking-[.08em] text-white/50 md:block">{g.label}</p>}
              {g.items.map(it => (
                <button key={it.key} onClick={() => onNavigate(it.key)} aria-current={active === it.key ? 'page' : undefined}
                  className={cn('flex shrink-0 items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm',
                    active === it.key ? 'bg-pine font-semibold text-white shadow-[inset_3px_0_0_var(--color-tick)]' : 'text-white/80 hover:bg-pine')}>
                  {it.label}
                  {it.count !== undefined && <span className={cn('rounded-full px-2 text-xs tabular', it.urgent ? 'bg-bad font-semibold text-white' : 'bg-white/10')}
                    title={it.urgent ? 'Some are past their deadline' : undefined}>{it.count}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="hidden border-t border-white/10 px-5 py-4 md:block">
          <p className="text-sm font-semibold">{user.displayName}</p>
          {user.detail && <p className="text-xs text-white/60">{user.detail}</p>}
          <button onClick={onSignOut} disabled={signingOut} aria-busy={signingOut}
            className="mt-3 flex items-center gap-2 text-xs font-semibold text-lime hover:underline disabled:cursor-wait disabled:opacity-70 disabled:no-underline">
            {signingOut ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <LogOut className="size-3.5" aria-hidden />}
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
          {signOutError && <p role="alert" className="mt-2 text-xs text-white/80">{signOutError}</p>}
        </div>
      </nav>
      <div className="min-w-0 flex-1">
        {topBar}
        <main className={cn('mx-auto px-4 py-6 md:px-8', wide ? 'max-w-[1920px]' : 'max-w-[1280px]')}>
          {(crumbs.length > 1 || headerRight) && (
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <nav aria-label="Breadcrumb" className="pt-1 text-xs text-ink-3">
                {crumbs.length > 1 && crumbs.map((c, i) => (
                  <span key={i}>{i > 0 && <span aria-hidden>{crumbRoot ? ' › ' : ' / '}</span>}
                    {c.key !== undefined && i < crumbs.length - 1 ? <button className="hover:text-tick-ink hover:underline" onClick={() => onNavigate(c.key!)}>{c.label}</button> : c.label}</span>
                ))}
              </nav>
              {headerRight}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
    </CrumbContext.Provider>
  );
}
